import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Building2, MapPin, Stethoscope, Star, Filter, ArrowRight, ShieldCheck, CheckCircle2
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import * as bookingsApi from '../../api/bookings.api';
import { errorMessage } from '../../api/client';

// Customer-facing approved-clinic browser (spec §8 matching engine).
// Mounted at /clinics. When opened with ?orderId=X, each card gets a
// "Select for this booking" button that calls assignClinic and routes
// the user back to /orders/:id to pick a date.

function distanceLabel(meters) {
  if (typeof meters !== 'number') return null;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

function ClinicCard({ clinic, orderId, onSelect, busy }) {
  const cp = clinic.clinicProfile || {};
  const perf = cp.performance || {};
  const addr = [cp.addressLine1, cp.city, cp.state].filter(Boolean).join(', ');

  return (
    <div className="card" style={{ marginBottom: 'var(--s-3)' }}>
      <div className="row between" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ flex: '1 1 320px' }}>
          <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Building2 size={18} />
            <span className="bold" style={{ fontSize: 16 }}>{cp.clinicName || clinic.name}</span>
            <span className="badge success"><ShieldCheck size={12} /> Approved</span>
            {distanceLabel(clinic.distanceMeters) && (
              <span className="muted text-sm">· {distanceLabel(clinic.distanceMeters)}</span>
            )}
          </div>

          <div className="muted text-sm" style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <MapPin size={14} /> {addr || '—'}
          </div>

          <div className="grid grid-3" style={{ gap: 'var(--s-2) var(--s-4)', marginTop: 'var(--s-3)' }}>
            <div>
              <span className="muted text-xs">Prescribing doctor</span>
              <div className="row" style={{ gap: 6 }}><Stethoscope size={14} /> {cp.prescribingDoctor || <span className="muted">—</span>}</div>
            </div>
            <div>
              <span className="muted text-xs">Avg loss</span>
              <div className="bold">{perf.avgWeightLossKg ? `${perf.avgWeightLossKg.toFixed(1)} kg` : '—'}</div>
            </div>
            <div>
              <span className="muted text-xs">Rating</span>
              <div className="row" style={{ gap: 4 }}>
                <Star size={14} />
                {perf.customerRating ? perf.customerRating.toFixed(1) : '—'}
                <span className="muted text-xs">· {perf.totalCompleted || 0} completed</span>
              </div>
            </div>
          </div>
        </div>

        <div className="col" style={{ gap: 8, minWidth: 180 }}>
          {orderId ? (
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() => onSelect(clinic)}
            >
              <CheckCircle2 size={14} /> Select for booking
            </button>
          ) : (
            <Link className="btn btn-outline" to={`/clinics/${clinic._id}`}>View details <ArrowRight size={14} /></Link>
          )}
        </div>
      </div>
    </div>
  );
}

function ClinicsList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ city: '', state: '' });
  const [coords, setCoords] = useState({ lat: '', lng: '' });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const params = {};
        if (filters.city)  params.city = filters.city;
        if (filters.state) params.state = filters.state;
        if (coords.lat && coords.lng) { params.lat = coords.lat; params.lng = coords.lng; }
        const list = await bookingsApi.listPublicClinics(params);
        if (!cancelled) setItems(list || []);
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [filters, coords]);

  function useMyLocation() {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => toast.error('Could not get your location.'),
    );
  }

  async function selectClinic(clinic) {
    if (!orderId) return;
    setBusy(true);
    try {
      await bookingsApi.assignClinic(orderId, clinic._id);
      toast.success(`Selected ${clinic.clinicProfile?.clinicName || clinic.name}`);
      navigate(`/orders/${orderId}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title={orderId ? 'Pick a clinic for your booking' : 'Approved clinics'}
        subtitle={orderId
          ? 'These clinics are licensed and approved to administer GLP-1 treatment. Pick the one you want to visit.'
          : 'Browse partner clinics — every one is MMC + APC verified by our admin team.'}
      />

      <div className="card" style={{ marginBottom: 'var(--s-4)' }}>
        <div className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '1 1 200px', marginBottom: 0 }}>
            <label>City</label>
            <input className="input" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} placeholder="e.g. Kuala Lumpur" />
          </div>
          <div className="field" style={{ flex: '1 1 200px', marginBottom: 0 }}>
            <label>State</label>
            <input className="input" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} placeholder="e.g. Selangor" />
          </div>
          <button className="btn btn-outline" onClick={useMyLocation}><Filter size={14} /> Use my location</button>
          {(coords.lat || coords.lng) && (
            <span className="muted text-sm">Sorted by proximity ·{' '}
              <button className="btn-link" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--brand-700)' }} onClick={() => setCoords({ lat: '', lng: '' })}>clear</button>
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <EmptyState
          title="No approved clinics yet"
          description="Check back soon — clinics are added as they complete the approval flow."
        />
      ) : (
        items.map((c) => (
          <ClinicCard
            key={c._id}
            clinic={c}
            orderId={orderId}
            onSelect={selectClinic}
            busy={busy}
          />
        ))
      )}
    </>
  );
}

export default ClinicsList;
